import React, { useMemo, useState } from 'react'
import parse from 'html-react-parser'
import moment from 'moment'
import { FolderKanban, ListChecks, MessageSquare, Trash2 } from 'lucide-react'
import { cn } from 'lib/utils'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import ImageReviewPopup from 'components/ImageGallery/ImageReviewPopup'
import { splitNoteContent, isSameImageUrl } from 'utils/noteImages'
import NoteImageCarousel from './NoteImageCarousel'
import type { NoteImageRecord, NoteTag, WorkspaceNote } from './notesWorkspace.types'

interface NoteCardProps {
	note: WorkspaceNote
	/** Gallery rows for the surface, matched to note images by URL. */
	imageRecords: NoteImageRecord[]
	currentUserId: string
	/** Replies render tighter and without their own reply affordance. */
	isReply?: boolean
	/** Hidden entirely on portfolio notes, which cannot thread. */
	canReply?: boolean
	replyCount?: number
	onReply?: (parentId: string) => void
	onToggleReplies?: () => void
	repliesExpanded?: boolean
	/**
	 * Whether to offer the delete control. Already combines "who" (the surface's
	 * `canDeleteNote`) and "when" (no replies), both decided by the workspace, so
	 * the card renders the answer rather than working it out.
	 */
	canDelete?: boolean
	onDelete?: () => void
	/** "Make tasks" from this note with Ask MDT (MYD-643); absent, no control. */
	onMakeTasks?: () => void
	onUpdateImage?: (image: NoteImageRecord) => void | Promise<void>
	onRemoveImage?: (args: {
		image: NoteImageRecord
		url: string
		noteId?: string
		noteBody: string
	}) => void | Promise<void>
	/**
	 * Where the note came from, shown top-right. Used on a location's Notes tab
	 * to name the project a note was written against, since that tab shows the
	 * location's own notes and its projects' notes in one feed.
	 */
	tag?: NoteTag | null
	/** Opens the review dialog at this index as soon as the card mounts. */
	openImageIndex?: number | null
	onImageReviewClose?: () => void
	className?: string
}

const formatDate = (createdAt?: string) => {
	if (!createdAt) return ''
	const when = moment(createdAt)
	if (when.isSame(moment(), 'day')) return when.format('h:mm a')
	if (when.isAfter(moment().subtract(7, 'days'))) return when.format('ddd, h:mm a')
	if (when.isSame(moment(), 'year')) return when.format('MMM D')
	return when.format('MMM D, YYYY')
}

const initialsOf = (name?: string): string => {
	if (!name) return 'U'
	const parts = name.trim().split(/\s+/)
	if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
	return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
}

const ownerIdOf = (record?: NoteImageRecord | null): string | undefined => {
	const owner = record?.userid
	if (!owner) return undefined
	return typeof owner === 'string' ? owner : owner._id
}

/**
 * One note.
 *
 * Rearranged from the feed card this replaced. Each difference answers a
 * specific weakness of that original:
 *
 * - The card owns its own spacing. The legacy `.note` class is not used, so the
 *   `margin-bottom: 0` in styles/css/LocationNote.css no longer welds every note
 *   to the next one. That stylesheet is still loaded for Posts and Reports.
 * - The meta row is one line. Name and time sit together and the email moves to
 *   the name's tooltip, recovering the second line the old two-line header spent.
 * - Prose keeps the full card width, with images full bleed beneath it.
 * - Replies get a real count and a thread line instead of rendering flat and
 *   unlabelled.
 */
const NoteCard: React.FC<NoteCardProps> = ({
	note,
	imageRecords,
	currentUserId,
	tag = null,
	isReply = false,
	canReply = true,
	replyCount = 0,
	onReply,
	onToggleReplies,
	repliesExpanded,
	canDelete = false,
	onDelete,
	onMakeTasks,
	onUpdateImage,
	onRemoveImage,
	openImageIndex = null,
	onImageReviewClose,
	className,
}) => {
	const [reviewIndex, setReviewIndex] = useState<number | null>(null)

	const noteBody = note.note || ''
	// Shared with the field feed, which makes the same split for a carousel.
	const { textHtml, imageUrls } = useMemo(() => splitNoteContent(noteBody), [noteBody])
	const hasImages = imageUrls.length > 0

	const showReplyControls = !isReply && canReply
	const showDelete = Boolean(canDelete && onDelete)
	const hasFooter = showReplyControls || showDelete || Boolean(onMakeTasks)

	// Memoised because this array's identity reaches the review dialog: rebuilt
	// every render, the dialog reads it as a different set and resets which
	// image is showing.
	const reviewImages = useMemo<NoteImageRecord[]>(
		() =>
			imageUrls.map((url, index) => {
				const record = (imageRecords || []).find(candidate =>
					isSameImageUrl(candidate.url, url),
				)
				return (
					record ?? {
						_id: `unlinked-${index}`,
						url,
						note: '',
						type: '',
						__unlinked: true,
					}
				)
			}),
		[imageUrls, imageRecords],
	)

	// The rail can ask for a specific image; otherwise the card's own clicks win.
	const activeIndex = reviewIndex !== null ? reviewIndex : openImageIndex
	const activeImage = activeIndex === null ? null : reviewImages[activeIndex] ?? null
	const isUnlinked = Boolean(activeImage?.__unlinked)
	const isUploader = !!currentUserId && !!activeImage && ownerIdOf(activeImage) === currentUserId

	// Mirrors the gallery's rule: only the uploader may change or remove an
	// image. Everyone else keeps preview and cycling.
	const canEdit = !isUnlinked && isUploader && !!onUpdateImage
	const canRemove = !isUnlinked && isUploader && !!onRemoveImage

	const disabledReason = isUnlinked
		? 'This image is not linked to a gallery record, so it can only be viewed.'
		: 'Only the person who uploaded this image can change it.'

	const closeReview = () => {
		setReviewIndex(null)
		onImageReviewClose?.()
	}

	const authorName = note.userid?.name || note.userid?.firstName || 'Unknown'

	return (
		<div
			id={`note-${note._id}`}
			data-testid={isReply ? 'note-reply' : 'note-card'}
			data-image-count={imageUrls.length}
			className={cn(
				'group scroll-mt-24 overflow-hidden bg-card transition-shadow',
				isReply
					? 'rounded-lg border border-border/70'
					: 'rounded-xl border border-border shadow-sm hover:shadow-md',
				className,
			)}
		>
			{/* Meta row: avatar, author, time */}
			<div className={cn('flex items-center gap-3', isReply ? 'px-4 pt-3' : 'px-5 pt-4')}>
				<Avatar className={cn(isReply ? 'h-7 w-7' : 'h-9 w-9')}>
					<AvatarImage src={note.userid?.image} alt={authorName} />
					<AvatarFallback className="text-xs font-medium">
						{initialsOf(authorName)}
					</AvatarFallback>
				</Avatar>

				<div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
					<span
						className={cn(
							'truncate font-semibold text-foreground',
							isReply ? 'text-xs' : 'text-sm',
						)}
						title={note.userid?.email}
					>
						{authorName}
					</span>
					<span className="text-xs text-muted-foreground">{formatDate(note.createdAt)}</span>
				</div>

				{tag && (
					<span
						data-testid="note-tag"
						title={tag.title || tag.label}
						className="inline-flex max-w-[45%] shrink-0 items-center gap-1 self-start rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-secondary-foreground"
					>
						<FolderKanban className="h-3 w-3 shrink-0" />
						<span className="truncate">{tag.label}</span>
					</span>
				)}
			</div>

			{/* Prose, at full card width */}
			{textHtml.trim() && (
				<div
					className={cn(
						'text-foreground/90 [&_a]:text-primary [&_a]:underline [&_p]:m-0 [&_p+p]:mt-2 [&_ul]:my-2 [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:pl-5 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:italic',
						// Long unbroken strings (pasted URLs) must wrap rather than
						// stretch the card and force the page to scroll sideways.
						'[overflow-wrap:anywhere]',
						isReply ? 'px-4 pt-2 text-xs leading-relaxed' : 'px-5 pt-3 text-sm leading-relaxed',
					)}
				>
					{parse(textHtml)}
				</div>
			)}

			{/* Images, full bleed */}
			{hasImages && (
				<NoteImageCarousel
					urls={imageUrls}
					onSelect={setReviewIndex}
					className={cn(textHtml.trim() ? 'mt-3' : 'mt-3', isReply && 'mt-2')}
				/>
			)}

			{/*
			 * Footer: reply count, the reply affordance, and delete.
			 *
			 * The condition used to be `!isReply && canReply`, which was right while
			 * replying was the only thing a footer held. Delete is offered on replies
			 * and on portfolio notes too — neither of which can reply — so a footer
			 * now appears whenever there is anything to put in it (MYD-564).
			 */}
			{hasFooter && (
				<div
					className={cn(
						'flex items-center gap-1 px-5 py-2',
						hasImages ? 'border-t border-border/60' : 'pt-2',
					)}
				>
					{showReplyControls && replyCount > 0 && (
						<button
							type="button"
							onClick={onToggleReplies}
							className="rounded-md px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
						>
							{repliesExpanded ? 'Hide' : 'Show'} {replyCount}{' '}
							{replyCount === 1 ? 'reply' : 'replies'}
						</button>
					)}

					{/*
					 * Both controls stay hidden until the note is hovered or focused,
					 * so a quiet feed stays quiet. `focus-visible:opacity-100` is what
					 * keeps them reachable by keyboard, which matters more for delete
					 * than for reply: an action nobody can tab to is not an action.
					 */}
					<div className="ml-auto flex items-center gap-1">
						{onMakeTasks && (
							<button
								type="button"
								onClick={onMakeTasks}
								aria-label="Make tasks from this note"
								className={cn(
									'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-all hover:bg-muted hover:text-foreground',
									'opacity-0 focus-visible:opacity-100 group-hover:opacity-100',
								)}
							>
								<ListChecks className="h-3.5 w-3.5" />
								Make tasks
							</button>
						)}

						{showReplyControls && (
							<button
								type="button"
								onClick={() => onReply?.(note._id)}
								aria-label="Reply to this note"
								className={cn(
									'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-all hover:bg-muted hover:text-foreground',
									'opacity-0 focus-visible:opacity-100 group-hover:opacity-100',
								)}
							>
								<MessageSquare className="h-3.5 w-3.5" />
								Reply
							</button>
						)}

						{showDelete && (
							<button
								type="button"
								onClick={onDelete}
								aria-label="Delete this note"
								data-testid="note-delete"
								className={cn(
									'inline-flex items-center justify-center rounded-full p-1.5 text-muted-foreground transition-all hover:bg-destructive/10 hover:text-destructive',
									'opacity-0 focus-visible:opacity-100 group-hover:opacity-100',
								)}
							>
								<Trash2 className="h-3.5 w-3.5" />
							</button>
						)}
					</div>
				</div>
			)}

			{/* Spacer for cards with no footer, so content never touches the edge */}
			{!hasFooter && <div className={isReply ? 'pb-3' : 'pb-4'} />}

			<ImageReviewPopup
				open={activeIndex !== null}
				onClose={closeReview}
				image={activeImage}
				images={reviewImages}
				disableUpdate={!canEdit}
				disableUpdateTitle={disabledReason}
				disableDelete={!canRemove}
				disableDeleteTitle={disabledReason}
				onUpdateNote={async (updated: NoteImageRecord) => {
					await onUpdateImage?.(updated)
					closeReview()
				}}
				onDeleteImage={async (removed: NoteImageRecord) => {
					await onRemoveImage?.({
						image: removed,
						url: removed.url,
						noteId: note._id,
						noteBody,
					})
					closeReview()
				}}
			/>
		</div>
	)
}

export default NoteCard
